import React from "react";
import Grid from "@mui/material/Grid";
import ReminderIcon from "@/src/assets/icons/contract/toggleable/IconTime";
import DebtCollectionIcon from "@/src/assets/icons/contract/toggleable/IconCompany";
import PaymentOrderIcon from "@/src/assets/icons/provision/toggleable/IconPostbox";
import LawsuitIcon from "@/src/assets/icons/generic/toggleable/IconCourtDetailed";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import { translate } from "@/src/services/translations/translations";
import MoneyIcon from "@/src/assets/icons/contract/toggleable/IconMoneyEnforce";
import GoodsIcon from "@/src/assets/icons/contract/toggleable/IconGoodsEnforce";
import { isMoney, requiredValidator } from "@/src/services/validationRules";
import { useForm } from "@/src/provider/Form/FormProvider";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Group from "@/src/components/Wizard/Group";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import MoneyAndGoodsIcon from "@/src/assets/icons/contract/toggleable/IconMoneyAndGoodsEnforce";
import DefenseProcessData from "@/src/components/Form/ContractClaim/DefenseProcesData";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";

type ContractBaseDefendClaimProps = {
  product: AbstractCase;
};

export default function ContractBaseDefendClaim({ product }: ContractBaseDefendClaimProps) {
  const { values } = useForm();

  return (
    <>
      <Grid size={12}>
        <Group
          type="radio"
          name="defenseProcessStatus"
          question={translate(`rentalContract.defenseProcessStatus.question`, values)}
          options={[
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.none.label`, values),
              labelIcon: <CrossIcon />,
              labelIconActive: <CrossIcon active />,
              value: "none",
            },
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.reminder.label`, values),
              labelIcon: <ReminderIcon />,
              labelIconActive: <ReminderIcon active />,
              value: "reminder",
            },
            ...(values.contractPartyType === "tenant"
              ? [
                  {
                    labelText: translate(`rentalContract.defenseProcessStatus.options.debtCollection.label`, values),
                    labelIcon: <DebtCollectionIcon />,
                    labelIconActive: <DebtCollectionIcon active />,
                    value: "debtCollection",
                  },
                ]
              : []),
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.paymentOrder.label`, values),
              labelIcon: <PaymentOrderIcon />,
              labelIconActive: <PaymentOrderIcon active />,
              optionHintText: "Ein Mahnbescheid kommt immer in einem gelben Brief per Post.",
              value: "paymentOrder",
            },
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.enforcementOrder.label`, values),
              labelIcon: <PaymentOrderIcon />,
              labelIconActive: <PaymentOrderIcon active />,
              optionHintText: "Ein Vollstreckungsbescheid kommt immer in einem gelben Brief per Post.",
              value: "enforcementOrder",
            },
            {
              labelText: translate(`rentalContract.defenseProcessStatus.options.lawsuit.label`, values),
              labelIcon: <LawsuitIcon />,
              labelIconActive: <LawsuitIcon active />,
              optionHintText: "Sie haben wegen der Forderung gegen Sie ein Klageschreiben vom Gericht zugestellt bekommen.",
              value: "lawsuit",
            },
          ]}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      <DefenseProcessData product={product} />
      <Grid size={12}>
        <Group
          type="radio"
          name="subjectOfDispute"
          question={translate(`rentalContract.subjectOfDispute.question`, values)}
          options={[
            {
              labelText: translate(`rentalContract.subjectOfDispute.options.money.label`, values),
              labelIcon: <MoneyIcon />,
              labelIconActive: <MoneyIcon active />,
              value: "money",
            },
            {
              labelText: translate(`rentalContract.subjectOfDispute.options.goods.label`, values),
              labelIcon: <GoodsIcon />,
              labelIconActive: <GoodsIcon active />,
              value: "goods",
            },
            {
              labelText: translate("rentalContract.subjectOfDispute.options.moneyAndGoods.label", values),
              labelIcon: <MoneyAndGoodsIcon sx={{ width: 300 }} />,
              labelIconActive: <MoneyAndGoodsIcon active sx={{ width: 300 }} />,
              value: "moneyAndGoods",
            },
          ]}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      {!!values.defenseProcessStatus && values.subjectOfDispute === "money" && (
        <>
          <Grid size={12}>
            <FormLegend>{translate("rentalContract.claimedAmountClient.question", values)}</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorTextField
              label={translate("rentalContract.claimedAmountClient.label", values)}
              name="claimedAmountClient"
              validators={[
                {
                  validator: isMoney,
                  message: "Bitte geben Sie den Wert in Euro und Cent ein - zum Beispiel 136,15 oder 85,00",
                },
              ]}
              helperText={translate("rentalContract.claimedAmountClient.helperText", values)}
            />
          </Grid>
        </>
      )}
    </>
  );
}
